<template>
  <div class="min-h-screen bg-midnight-900 text-steel-100">
    <header v-if="!isAuthRoute" class="sticky top-0 z-50 border-b border-white/5 bg-midnight-900/70 backdrop-blur py-4">
      <div class="mx-auto flex max-w-6xl flex-col gap-4 px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="flex items-center justify-between gap-3">
          <router-link class="flex items-center gap-3" to="/feed">
            <div class="flex h-9 w-9 items-center justify-center overflow-hidden rounded-xl bg-white/10 ring-1 ring-white/15">
              <img
                src="@/assets/logoDRVN.png"
                alt="DRVN logo"
                class="h-full w-full object-cover"
              />
            </div>
            <div>
              <p class="font-display text-sm uppercase tracking-[0.35em] text-steel-300">DRVN</p>
              <p class="text-xs text-steel-400">Modern automotive lifestyle</p>
            </div>
          </router-link>
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/5 p-2 text-steel-200 hover:text-white sm:hidden"
            @click="isNavOpen = !isNavOpen"
          >
            <svg v-if="!isNavOpen" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M4 6h16M4 12h16M4 18h16" stroke-width="1.6" stroke-linecap="round" />
            </svg>
            <svg v-else class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M6 6l12 12M18 6l-12 12" stroke-width="1.6" stroke-linecap="round" />
            </svg>
          </button>
        </div>

        <nav class="hidden flex-wrap items-center gap-4 text-xs uppercase tracking-[0.3em] text-steel-300 sm:flex">
          <form class="relative" v-if="isAuthenticated" @submit.prevent="handleSearch">
            <input
              v-model.trim="searchQuery"
              type="text"
              placeholder="Search handle or name"
              class="w-44 rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-2 text-xs uppercase tracking-[0.3em] text-steel-200 placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30 sm:w-56"
              @input="queueSearch"
            />
            <div
              v-if="showSearchResults"
              class="absolute left-0 top-11 z-20 w-64 rounded-2xl border border-white/10 bg-midnight-900/95 p-2 text-xs text-steel-200 shadow-glow backdrop-blur"
            >
              <button
                v-for="user in searchResults"
                :key="user.handle"
                type="button"
                class="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-white/5"
                @click="goToUser(user.handle)"
              >
                <div class="h-8 w-8 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                  <img
                    v-if="user.avatar"
                    :src="user.avatar"
                    alt="User avatar"
                    class="h-full w-full object-cover"
                    referrerpolicy="no-referrer"
                    crossorigin="anonymous"
                  />
                </div>
                <div>
                  <p class="text-sm text-white">{{ user.fullName || 'DRVN Member' }}</p>
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ user.handle }}</p>
                </div>
              </button>
              <p v-if="!searchResults.length && !searching" class="px-3 py-2 text-[10px] uppercase tracking-[0.3em] text-steel-400">
                No user found
              </p>
              <p v-if="searching" class="px-3 py-2 text-[10px] uppercase tracking-[0.3em] text-steel-400">
                Searching...
              </p>
            </div>
          </form>
          <router-link v-if="!isAuthenticated" class="hover:text-white" to="/login">Login</router-link>
          <router-link v-if="!isAuthenticated" class="hover:text-white" to="/register">Register</router-link>
          <router-link v-if="isAuthenticated" class="hover:text-white" to="/feed">Feed</router-link>
          <router-link v-if="isAuthenticated" class="hover:text-white" to="/tournament">Tournament</router-link>
          <router-link v-if="isAuthenticated" class="relative inline-flex items-center" to="/notifications">
            <svg class="h-5 w-5 text-steel-300 hover:text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span
              v-if="unreadCount"
              class="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white transform transition-transform"
              :class="showTransientBadge ? 'pop-animate' : ''"
            >
              {{ unreadCount }}
            </span>
          </router-link>
          <div v-if="isAuthenticated" class="relative">
            <button
              type="button"
              class="inline-flex items-center gap-3 rounded-full hover:bg-white/5 p-1"
              @click="accountMenuOpen = !accountMenuOpen"
              aria-haspopup="true"
              :aria-expanded="accountMenuOpen"
            >
              <div class="h-8 w-8 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
              <img
                v-if="displayAvatarUrl"
                :src="displayAvatarUrl"
                alt="Profile avatar"
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
                <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">{{ initials }}</div>
              </div>
            </button>

            <div v-if="accountMenuOpen" class="absolute right-0 mt-2 w-44 rounded-2xl border border-white/10 bg-midnight-900/95 p-2 shadow-glow">
              <router-link class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :to="`/u/${publicProfileHandle}`" @click="accountMenuOpen = false">Profile</router-link>
              <router-link class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" to="/profile" @click="accountMenuOpen = false">Settings</router-link>
              <router-link v-if="isAdmin" class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" to="/admin" @click="accountMenuOpen = false">Admin</router-link>
              <router-link class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" to="/report" @click="accountMenuOpen = false">Report issue</router-link>
              <button class="block w-full text-left rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" @click="handleLogoutFromMenu">Log out</button>
            </div>
          </div> 
        </nav>
      </div>
      <nav
        v-if="isNavOpen"
        class="flex flex-col items-center gap-3 border-t border-white/5 px-6 pb-4 text-center text-xs uppercase tracking-[0.3em] text-steel-300 sm:hidden"
      >
        <form class="relative w-full max-w-xs pt-4" v-if="isAuthenticated" @submit.prevent="handleSearch">
          <input
            v-model.trim="searchQuery"
            type="text"
            placeholder="Search handle or name"
            class="w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-2 text-xs uppercase tracking-[0.3em] text-steel-200 placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            @input="queueSearch"
          />
          <div
            v-if="showSearchResults"
            class="absolute left-0 top-11 z-20 w-full rounded-2xl border border-white/10 bg-midnight-900/95 p-2 text-xs text-steel-200 shadow-glow backdrop-blur"
          >
            <button
              v-for="user in searchResults"
              :key="user.handle"
              type="button"
              class="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-white/5"
              @click="goToUser(user.handle)"
            >
              <div class="h-8 w-8 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                <img
                  v-if="user.avatar"
                  :src="user.avatar"
                  alt="User avatar"
                  class="h-full w-full object-cover"
                  referrerpolicy="no-referrer"
                  crossorigin="anonymous"
                />
              </div>
              <div>
                <p class="text-sm text-white">{{ user.fullName || 'DRVN Member' }}</p>
                <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ user.handle }}</p>
              </div>
            </button>
            <p v-if="!searchResults.length && !searching" class="px-3 py-2 text-[10px] uppercase tracking-[0.3em] text-steel-400">
              No user found
            </p>
            <p v-if="searching" class="px-3 py-2 text-[10px] uppercase tracking-[0.3em] text-steel-400">
              Searching...
            </p>
          </div>
        </form>
        <router-link v-if="!isAuthenticated" class="hover:text-white pt-6" to="/login">Login</router-link>
        <router-link v-if="!isAuthenticated" class="hover:text-white" to="/register">Register</router-link>
        <router-link v-if="isAuthenticated" class="hover:text-white" to="/feed">Feed</router-link>
        <router-link v-if="isAuthenticated" class="hover:text-white" to="/tournament" @click="isNavOpen = false">Tournament</router-link>
        <router-link v-if="isAuthenticated" class="relative inline-flex items-center" to="/notifications">
          <span>Notifications</span>
          <span
            v-if="unreadCount"
            class="ml-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white transform transition-transform"
            :class="showTransientBadge ? 'pop-animate' : ''"
          >
            {{ unreadCount }}
          </span>
        </router-link>
        <router-link v-if="isAuthenticated" class="hover:text-white" :to="`/u/${publicProfileHandle}`" @click="isNavOpen = false">Profile</router-link>
        <details v-if="isAuthenticated" class="w-full max-w-xs">
          <summary class="mx-auto w-full cursor-pointer list-none rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-center text-[10px] font-semibold uppercase tracking-[0.3em] text-steel-200">
            More
          </summary>
          <div class="mt-3 flex flex-col items-center gap-3">
            <router-link class="hover:text-white" to="/profile" @click="isNavOpen = false">Settings</router-link>
            <router-link v-if="isAdmin" class="hover:text-white" to="/admin" @click="isNavOpen = false">Admin</router-link>
            <router-link class="hover:text-white" to="/report" @click="isNavOpen = false">Report issue</router-link>
            <button
              type="button"
              class="text-center hover:text-white"
              @click="showLogoutConfirm = true"
            >
              Logout
            </button>
          </div>
        </details>
      </nav>
    </header>

    <main>
      <router-view :key="routeKey" />
    </main>

    <cookie-consent />

    <div
      v-if="showLogoutConfirm"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-6"
      @click.self="showLogoutConfirm = false"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <h3 class="font-display text-xl font-semibold text-white">Log out?</h3>
        <p class="mt-3 text-sm text-steel-300">
          Are you sure you want to log out of your account?
        </p>
        <div class="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
            @click="showLogoutConfirm = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-2xl bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400"
            @click="confirmLogout"
          >
            Log out
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { supabase, getAvatarUrl } from '@/lib/supabase'
import CookieConsent from '@/components/CookieConsent.vue'

export default {
  name: 'App',
  components: {
    CookieConsent,
  },
  data() {
    return {
      session: null,
      unreadCount: 0,
      searchQuery: '',
      searchResults: [],
      searching: false,
      searchTimer: null,
      isNavOpen: false,
      accountMenuOpen: false,
      profile: {},
      showTransientBadge: false,
      transientBadgeTimer: null,
      installPromptEvent: null,
      canInstallPwa: false,
      isMobileDevice: false,
      showLogoutConfirm: false,
      routeKey: 0,
      profileUpdateHandler: null,
    }
  },
  computed: {
    isAuthRoute() {
      return Boolean(this.$route?.meta?.auth)
    },
    isAuthenticated() {
      return Boolean(this.session?.user)
    },
    isAdmin() {
      return this.profile?.role === 'admin'
    },
    showSearchResults() {
      return Boolean(this.searchQuery)
    },
    initials() {
      const full = this.profile?.full_name || this.session?.user?.user_metadata?.full_name || ''
      if (!full) return 'D'
      return full
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join('')
    },
    displayAvatarUrl() {
      const avatarPath = this.profile?.avatar_path || this.session?.user?.user_metadata?.avatar_path || ''
      const avatarUrl = this.profile?.avatar_url || this.session?.user?.user_metadata?.avatar_url || ''
      return getAvatarUrl({ avatarPath, avatarUrl })
    },    publicProfileHandle() {
      const handle = this.profile?.handle || this.session?.user?.user_metadata?.handle || ''
      if (handle) return handle
      const userId = this.session?.user?.id || ''
      return userId ? userId.slice(0, 8) : 'member'
    },  },
  async mounted() {
    const { data } = await supabase.auth.getSession()
    this.session = data?.session || null
    // Ensure a profiles row exists for OAuth sign-ins (Google, etc.)
    await this.ensureProfile(this.session)
    await this.refreshNotificationCount()
    await this.fetchProfile()

    supabase.auth.onAuthStateChange(async (_event, session) => {
      this.session = session
      // Create/upsert profiles row when a user signs in via OAuth
      await this.ensureProfile(session)
      this.refreshNotificationCount()
      this.fetchProfile()
    })

    const ua = navigator.userAgent || ''
    this.isMobileDevice = /Android|iPhone|iPad|iPod/i.test(ua)

    window.addEventListener('beforeinstallprompt', (event) => {
      event.preventDefault()
      this.installPromptEvent = event
      this.canInstallPwa = true
      window.__drvnInstallPrompt = event
    })

    window.addEventListener('appinstalled', () => {
      this.installPromptEvent = null
      this.canInstallPwa = false
      window.__drvnInstallPrompt = null
    })

    this.profileUpdateHandler = (event) => {
      const detail = event?.detail || {}
      if (detail.avatar_path || detail.avatar_url) {
        this.profile = {
          ...this.profile,
          avatar_path: detail.avatar_path ?? this.profile?.avatar_path,
          avatar_url: detail.avatar_url ?? this.profile?.avatar_url,
        }
        return
      }
      this.fetchProfile()
    }
    window.addEventListener('profile:updated', this.profileUpdateHandler)

    // Set up route watcher to reload components on navigation
    this.$router.afterEach(() => {
      this.routeKey += 1
    })
  },
  methods: {
    beforeDestroy() {
      if (this.transientBadgeTimer) {
        clearTimeout(this.transientBadgeTimer)
        this.transientBadgeTimer = null
      }
    },
    beforeUnmount() {
      if (this.transientBadgeTimer) {
        clearTimeout(this.transientBadgeTimer)
        this.transientBadgeTimer = null
      }
      if (this.profileUpdateHandler) {
        window.removeEventListener('profile:updated', this.profileUpdateHandler)
        this.profileUpdateHandler = null
      }
    },
    async refreshNotificationCount() {
      const userId = this.session?.user?.id
      if (!userId) {
        this.unreadCount = 0
        return
      }

      const { count } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('recipient_id', userId)
        .eq('is_read', false)

      this.unreadCount = count || 0

      // transient pop badge: show briefly for 5s when there are unread notifications
      if (this.unreadCount > 0) {
        this.showTransientBadge = true
        if (this.transientBadgeTimer) clearTimeout(this.transientBadgeTimer)
        this.transientBadgeTimer = setTimeout(() => {
          this.showTransientBadge = false
          this.transientBadgeTimer = null
        }, 5000)
      } else {
        if (this.transientBadgeTimer) {
          clearTimeout(this.transientBadgeTimer)
          this.transientBadgeTimer = null
        }
        this.showTransientBadge = false
      }
    },
    async fetchProfile() {
      const userId = this.session?.user?.id
      if (!userId) {
        this.profile = {}
        return
      }
      const { data, error } = await supabase
        .from('profiles')
        .select('handle, full_name, avatar_url, avatar_path, role, status')
        .eq('id', userId)
        .maybeSingle()
      if (!error && data) {
        this.profile = data
        if (data.status === 'banned') {
          await supabase.auth.signOut()
          this.session = null
          this.$router.push('/login?banned=1')
        }
      }
    },

    async ensureProfile(session) {
      try {
        const user = session?.user
        if (!user) return

        const userId = user.id
        // check if profile exists
        const { data: existing } = await supabase
          .from('profiles')
          .select('id, avatar_url')
          .eq('id', userId)
          .maybeSingle()

        // derive fields from user metadata or fallback to email prefix / id
        const meta = user.user_metadata || {}
        const handleFromMeta = (meta.handle && String(meta.handle).toLowerCase()) || ''
        const emailHandle = user.email ? String(user.email).split('@')[0].toLowerCase() : ''
        const handle = handleFromMeta || emailHandle || userId.slice(0, 8)
        const full_name = meta.full_name || meta.name || ''
        const avatar_url = meta.avatar_url || meta.picture || ''

        if (existing) {
          if (avatar_url && !existing.avatar_url) {
            await supabase.from('profiles').update({ avatar_url }).eq('id', userId)
          }
          return
        }

        await supabase
          .from('profiles')
          .upsert(
            {
              id: userId,
              handle,
              full_name,
              avatar_url,
            },
            { onConflict: 'id' }
          )
      } catch (e) {
        // ignore - profile creation is best-effort here
        console.warn('ensureProfile failed', e)
      }
    },
    handleLogoutFromMenu() {
      this.accountMenuOpen = false
      this.showLogoutConfirm = true
    },
    // close the account menu on route change
    watchRoute() {
      this.accountMenuOpen = false
    },

    async signOut() {
      await supabase.auth.signOut()
      window.location.href = '/'
    },
    async confirmLogout() {
      this.showLogoutConfirm = false
      await this.signOut()
    },
    handleSearch() {
      if (!this.searchQuery) return
      if (this.searchResults.length) {
        this.goToUser(this.searchResults[0].handle)
        return
      }
      const handle = this.searchQuery.replace(/^@/, '')
      this.$router.push(`/u/${handle}`)
      this.searchQuery = ''
    },
    queueSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      const query = this.searchQuery
      if (!query) {
        this.searchResults = []
        this.searching = false
        return
      }
      this.searching = true
      this.searchTimer = setTimeout(() => {
        this.fetchSearchResults(query)
      }, 250)
    },
    async fetchSearchResults(query) {
      const cleaned = query.replace(/^@/, '')
      const { data, error } = await supabase
        .from('profiles')
        .select('handle, full_name, avatar_url, avatar_path')
        .or(`handle.ilike.%${cleaned}%,full_name.ilike.%${cleaned}%`)
        .limit(5)

      if (error) {
        this.searchResults = []
        this.searching = false
        return
      }

      this.searchResults = (data || []).map((user) => {
        const avatar = getAvatarUrl({ avatarPath: user.avatar_path, avatarUrl: user.avatar_url })
        return {
          handle: user.handle,
          fullName: user.full_name,
          avatar,
        }
      })
      this.searching = false
    },
    goToUser(handle) {
      this.$router.push(`/u/${handle}`)
      this.searchQuery = ''
      this.searchResults = []
      this.searching = false
    },
  },
}
</script>

<style scoped>
.pop-animate {
  animation: pop 0.8s ease-in-out infinite;
  transform-origin: center;
}
@keyframes pop {
  0% { transform: scale(1); }
  50% { transform: scale(1.25); }
  100% { transform: scale(1); }
}
</style>
